if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.querySelector('[name=id]').disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.initialSubmitButtonDisabled = this.submitButton.disabled;

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.requiresSizeSelection = this.dataset.requireSizeSelection === 'true';
        this.selectSizeLabel = this.dataset.selectSizeLabel || window.variantStrings?.selectSize || 'Select a size';
        this.addToCartLabel = this.dataset.addToCartLabel || window.variantStrings?.addToCart || 'Add to cart';
        this.soldOutLabel = this.dataset.soldOutLabel || window.variantStrings?.soldOut || 'Sold out';
        this.sizeSelected = !this.requiresSizeSelection || this.initialSubmitButtonDisabled;
        this.productSection = this.closest('section');
        this.sizeGroup = this.findSizeGroup();
        this.onProductOptionChange = this.onProductOptionChange.bind(this);

        if (this.requiresSizeSelection && !this.initialSubmitButtonDisabled) {
          this.submitButton.removeAttribute('disabled');
          this.submitButton.dataset.stickyNeedsSize = 'true';
          this.setSubmitButtonLabel(this.selectSizeLabel);
          this.productSection?.addEventListener('change', this.onProductOptionChange);
          this.productSection?.addEventListener('click', this.onProductOptionChange);
        }
      }

      disconnectedCallback() {
        this.productSection?.removeEventListener('change', this.onProductOptionChange);
        this.productSection?.removeEventListener('click', this.onProductOptionChange);
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.dataset.stickyNeedsSize === 'true') {
          this.triggerSelectSizeFeedback();
          return;
        }

        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading-overlay__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButton.querySelector('span').classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: formData.get('id'), cartData: response });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading-overlay__spinner').classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      findSizeGroup() {
        if (!this.productSection) return null;

        const explicitSizeGroup = this.productSection.querySelector('.product-form__input.size');
        if (explicitSizeGroup) return explicitSizeGroup;

        const sizeInput = [...this.productSection.querySelectorAll('input[type="radio"], select')].find((input) => {
          const name = input.getAttribute('name')?.toLowerCase();
          return name === 'size' || name === 'options[size]';
        });
        return sizeInput?.closest('.product-form__input') || null;
      }

      onProductOptionChange(event) {
        if (!this.requiresSizeSelection || this.sizeSelected || !this.sizeGroup) return;

        const target = event.target;
        const label = target.closest?.('label');
        const labelFor = label?.getAttribute('for');
        const escapedLabelFor = labelFor && window.CSS?.escape ? CSS.escape(labelFor) : labelFor;
        const changedSizeInput = target.matches?.('input[type="radio"], select') && this.sizeGroup.contains(target);
        const clickedSizeLabel = label && this.sizeGroup.contains(label) && escapedLabelFor && this.sizeGroup.querySelector(`#${escapedLabelFor}`);
        const sizeTarget = changedSizeInput || clickedSizeLabel;

        if (!sizeTarget) return;

        const sizeInput = changedSizeInput ? target : this.sizeGroup.querySelector(`#${escapedLabelFor}`);
        this.sizeSelected = true;
        this.restoreSubmitButtonState(sizeInput?.classList.contains('disabled') || sizeInput?.disabled);
      }

      setSubmitButtonLabel(label) {
        const submitButtonLabel = this.submitButton?.querySelector('span');
        if (submitButtonLabel) submitButtonLabel.textContent = label;
      }

      restoreSubmitButtonState(sizeIsUnavailable) {
        if (!this.submitButton || this.submitButton.dataset.stickyNeedsSize !== 'true') return;

        delete this.submitButton.dataset.stickyNeedsSize;

        if (sizeIsUnavailable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          this.setSubmitButtonLabel(this.soldOutLabel);
          return;
        }

        this.submitButton.removeAttribute('disabled');
        this.setSubmitButtonLabel(this.addToCartLabel);
      }

      triggerSelectSizeFeedback() {
        if (!this.submitButton) return;

        this.submitButton.classList.remove('product-form__submit--wiggle');
        void this.submitButton.offsetWidth;
        this.submitButton.classList.add('product-form__submit--wiggle');

        window.setTimeout(() => {
          this.submitButton.classList.remove('product-form__submit--wiggle');
        }, 600);
      }
    }
  );
}
